import * as React from "react";
import { createSelector, weakMapMemoize } from "reselect";

import {
  EphemeralWorkspaceState,
  EphemeralWorkspaceStateContext,
} from "@/shared/components/visualization/WorkspaceCtx/EphemeralWorkspaceState";
import { AssociationType } from "@/shared/domain/association.ts";
import { FileRecord } from "@/shared/domain/files";
import { constructTree, TopicNode, TopicRecord } from "@/shared/domain/topics";
import {
  TimeBounds,
  determineTimeBounds,
} from "@/shared/domain/topics/determineTimeBounds";
import { useVizState } from "@/shared/state/visualization";
import { diffVizState } from "@/shared/state/visualization/diffVizState";

import { MatchError } from "../layouts/LayoutToVizConfig";

export const useEphemeralWorkspaceState = (): EphemeralWorkspaceState => {
  return React.useContext(EphemeralWorkspaceStateContext);
};

const selectDatasetIdsForFiles =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [(state) => state, (_, fileIds: string[]) => fileIds],
    (state, fileIds) => {
      return state.files
        .filter((file) => fileIds.includes(file.file_id))
        .map((file) => file.association_id);
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: string[], b: string[]) {
          if (a.length !== b.length) {
            return false;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
              return false;
            }
          }

          return true;
        },
      },
    },
  );

const selectFilesForTopics =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [(state) => state, (_, topicIds: string[]) => topicIds],
    (state, topicIds) => {
      const fileIds: string[] = state.topics
        .filter(
          (topic) =>
            topic.association.association_type === AssociationType.File &&
            topicIds.includes(topic.topic_id),
        )
        .map((topic) => topic.association.association_id);

      return state.files.filter((file) => fileIds.includes(file.file_id));
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: FileRecord[], b: FileRecord[]) {
          if (a.length !== b.length) {
            return false;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i].file_id !== b[i].file_id) {
              return false;
            }
          }

          return true;
        },
      },
    },
  );

const selectTopicsForFile = createSelector.withTypes<EphemeralWorkspaceState>()(
  [(state) => state, (_, fileId: string) => fileId],
  (state, fileId) => {
    return state.topics.filter(
      (topic) => fileId === topic.association.association_id,
    );
  },
  {
    memoizeOptions: {
      resultEqualityCheck(a: TopicRecord[], b: TopicRecord[]) {
        if (a.length !== b.length) {
          return false;
        }
        for (let i = 0; i < a.length; i++) {
          if (a[i].topic_id !== b[i].topic_id) {
            return false;
          }
        }

        return true;
      },
    },
    memoize: weakMapMemoize,
    argsMemoize: weakMapMemoize,
  },
);

const constructTreeForTopics =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [selectTopicsForFile],
    (topics: TopicRecord[]) => {
      return constructTree(topics);
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: TopicNode[], b: TopicNode[]) {
          if (a.length !== b.length) {
            return false;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id) {
              return false;
            }
          }

          return true;
        },
      },
      memoize: weakMapMemoize,
      argsMemoize: weakMapMemoize,
    },
  );

const selectTimeBoundsForTopics =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [
      (state) => state.topics,
      (_, fileIds?: string[]) => {
        return fileIds;
      },
    ],
    (topics, fileIds) => {
      let filteredTopics = topics;
      if (fileIds && fileIds.length > 0) {
        const fileIdsSet = new Set(fileIds);
        filteredTopics = topics.filter((topic) =>
          fileIdsSet.has(topic.association.association_id),
        );
      }
      return determineTimeBounds(filteredTopics);
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: TimeBounds, b: TimeBounds) {
          return a.earliest === b.earliest && a.latest === b.latest;
        },
      },
      memoize: weakMapMemoize,
      argsMemoize: weakMapMemoize,
    },
  );

export const useTopicTreeForFile = (fileId?: string): TopicNode[] => {
  const state = useEphemeralWorkspaceState();

  if (fileId === undefined) {
    return [];
  }

  return constructTreeForTopics(state, fileId);
};

export const selectAllWorkspaceTopicTrees =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [(state) => state],
    (state) => {
      const fileIds = state.files.map((f) => f.file_id);
      return fileIds.flatMap((fileId) => constructTreeForTopics(state, fileId));
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: TopicNode[], b: TopicNode[]) {
          if (a.length !== b.length) {
            return false;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id) {
              return false;
            }
          }

          return true;
        },
      },
      memoize: weakMapMemoize,
      argsMemoize: weakMapMemoize,
    },
  );

export const useWorkspaceTimeBounds = (fileIds?: string[]): TimeBounds => {
  const state = useEphemeralWorkspaceState();
  return selectTimeBoundsForTopics(state, fileIds);
};

export const useWorkspaceTopicsForFile = (fileId?: string): TopicRecord[] => {
  const state = useEphemeralWorkspaceState();

  if (fileId === undefined) {
    return [];
  }

  return selectTopicsForFile(state, fileId);
};

export const useWorkspaceDatasetIdsForFiles = (fileIds: string[]) => {
  const state = useEphemeralWorkspaceState();
  return selectDatasetIdsForFiles(state, fileIds);
};

export const useWorkspaceFiles = (): FileRecord[] => {
  const state = useEphemeralWorkspaceState();
  return state.files;
};

export const useWorkspaceFilesForTopics = (
  topicIds: string[],
): FileRecord[] => {
  const state = useEphemeralWorkspaceState();
  return selectFilesForTopics(state, topicIds);
};

export const useWorkspaceMessagePathById = (messagePathId?: string) => {
  const state = useEphemeralWorkspaceState();

  if (messagePathId === undefined) {
    return undefined;
  }

  return state.messagePaths.find(
    (messagePath) => messagePath.message_path_id === messagePathId,
  );
};

export const useWorkspaceTopicById = (topicId?: string) => {
  const state = useEphemeralWorkspaceState();

  if (topicId === undefined) {
    return undefined;
  }

  return state.topics.find((topic) => topic.topic_id === topicId);
};

export const useWorkspaceTopicByMessagePathId = (messagePathId?: string) => {
  const state = useEphemeralWorkspaceState();
  const messagePath = useWorkspaceMessagePathById(messagePathId);

  if (messagePath === undefined) {
    return undefined;
  }

  return state.topics.find((topic) => topic.topic_id === messagePath.topic_id);
};

export const useEphemeralWorkspaceStateLoading = () => {
  const state = useEphemeralWorkspaceState();
  return state.isDataLoading;
};

export const useWorkspaceError = () => {
  const state = useEphemeralWorkspaceState();
  return state.error;
};

export const useSelectedPanelId = () => {
  const state = useEphemeralWorkspaceState();
  return state.selectedPanelId;
};

export const useSelectPanel = (): EphemeralWorkspaceState["selectPanel"] => {
  const state = useEphemeralWorkspaceState();
  return state.selectPanel;
};

export const useWorkspaceTimer = () => {
  const state = useEphemeralWorkspaceState();
  return state.timer;
};

export function useActiveLayout(): [
  EphemeralWorkspaceState["activeLayout"],
  EphemeralWorkspaceState["setActiveLayout"],
] {
  const { activeLayout, setActiveLayout } = useEphemeralWorkspaceState();
  return [activeLayout, setActiveLayout];
}

export function useDataNotFoundErrors(
  panelId: string,
): MatchError[] | undefined {
  const { dataMatchingErrors } = useEphemeralWorkspaceState();
  return dataMatchingErrors[panelId];
}

export function useFileIdToTopics() {
  const workspaceState = useEphemeralWorkspaceState();
  const allTopics = selectAllWorkspaceTopicTrees(workspaceState);
  const fileIdToTopics: Record<string, TopicNode[]> = React.useMemo(() => {
    const fileIdToTopics: Record<string, TopicNode[]> = {};
    for (const t of allTopics) {
      fileIdToTopics[t.data.association.association_id] ??= [];
      fileIdToTopics[t.data.association.association_id].push(t);
    }
    return fileIdToTopics;
  }, [allTopics]);
  return fileIdToTopics;
}

export function useWorkspaceSnapshot(): [
  EphemeralWorkspaceState["workspaceSnapshot"],
  EphemeralWorkspaceState["setWorkspaceSnapshot"],
] {
  const { workspaceSnapshot, setWorkspaceSnapshot } =
    useEphemeralWorkspaceState();
  return [workspaceSnapshot, setWorkspaceSnapshot];
}

export function useUnsavedChanges() {
  const [initialState] = useWorkspaceSnapshot();
  const currentState = useVizState();

  const hasUnsavedChanges = React.useCallback(() => {
    const diff = diffVizState(initialState, currentState);
    return diff.hasChanges;
  }, [initialState, currentState]);

  return {
    hasUnsavedChanges,
  };
}
